import { useState } from "react";
import {
  Search,
  UserRound,
  Trophy,
  Users,
  Gamepad2,
  Crosshair,
  Medal,
  Crown,
  Calendar,
  MapPin,
  Swords,
  Target,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import api from "@/lib/api";
import { IMG } from "@/lib/assets";

const TYPE_LABEL = {
  SAF: "Seals Allies Force",
  INTERNAL_SEALS: "Seals",
};

const STATE_LABEL = {
  registration_open: "Iscrizioni aperte",
  registration_closed: "Iscrizioni chiuse",
  lobby_generated: "Lobby generate",
  in_progress: "In corso",
  completed: "Completato",
  archived: "Archiviato",
};

function fmtDate(value) {
  if (!value) return "Data non disponibile";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Data non disponibile";
  return date.toLocaleDateString("it-IT", {
    day: "2-digit",
    month: "long",
    year: "numeric",
  });
}

function valueOrDash(value) {
  return value === null || value === undefined ? "—" : value;
}

function StatCard({ icon: Icon, label, value, gold = false }) {
  return (
    <div className={gold ? "panel panel-gold clip-hud p-4" : "cyber-panel clip-hud p-4"}>
      <Icon
        className={`w-6 h-6 mb-2 ${gold ? "text-[var(--gold)]" : "text-[#67e8f9]"}`}
        strokeWidth={1.5}
      />
      <div className="font-oswald text-2xl text-white">{valueOrDash(value)}</div>
      <div className="font-rajdhani text-[10px] uppercase tracking-widest text-gray-400">
        {label}
      </div>
    </div>
  );
}

function TournamentHistory({ item, index }) {
  const [open, setOpen] = useState(index === 0);
  const tournament = item.tournament || {};
  const teams = item.teams || [];
  const results = item.results || [];
  const mvp = item.mvp || {};

  return (
    <article className="cyber-panel clip-hud overflow-hidden" data-testid={`history-tournament-${index}`}>
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        className="w-full p-5 sm:p-6 text-left hover:bg-white/[0.02] transition-colors"
      >
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2 mb-2">
              <span className="font-rajdhani uppercase tracking-wider text-[10px] px-2 py-1 bg-[#22d3ee] text-black clip-beveled-sm">
                {TYPE_LABEL[tournament.type] || tournament.type || "Torneo"}
              </span>
              <span className="font-rajdhani uppercase tracking-wider text-[10px] px-2 py-1 border border-white/15 text-gray-300 clip-beveled-sm">
                {STATE_LABEL[tournament.state] || tournament.state || "—"}
              </span>
              {tournament.season && (
                <span className="font-rajdhani uppercase tracking-wider text-[10px] text-gray-400">
                  {tournament.season}
                </span>
              )}
            </div>

            <h3 className="heading text-3xl sm:text-4xl text-white truncate">
              {tournament.name || "Torneo"}
            </h3>

            <div className="flex flex-wrap gap-x-5 gap-y-2 mt-3 font-rajdhani text-sm text-gray-400">
              <span className="inline-flex items-center gap-1.5">
                <Calendar className="w-4 h-4" />
                {fmtDate(tournament.start_at || tournament.created_at)}
              </span>
              {tournament.mode && (
                <span className="inline-flex items-center gap-1.5">
                  <Gamepad2 className="w-4 h-4" />
                  {tournament.mode}
                </span>
              )}
              {tournament.map && (
                <span className="inline-flex items-center gap-1.5">
                  <MapPin className="w-4 h-4" />
                  {tournament.map}
                </span>
              )}
            </div>
          </div>

          <div className="flex items-center gap-5 shrink-0">
            <div className="text-center">
              <div className="font-oswald text-2xl text-[#67e8f9]">
                {valueOrDash(item.ranking?.best_team_rank)}
              </div>
              <div className="font-rajdhani text-[10px] uppercase tracking-widest text-gray-500">
                Ranking
              </div>
            </div>
            <div className="text-center">
              <div className="font-oswald text-2xl text-[var(--gold-light)]">
                {valueOrDash(mvp.rank)}
              </div>
              <div className="font-rajdhani text-[10px] uppercase tracking-widest text-gray-500">
                MVP
              </div>
            </div>
            {open ? (
              <ChevronUp className="w-5 h-5 text-gray-400" />
            ) : (
              <ChevronDown className="w-5 h-5 text-gray-400" />
            )}
          </div>
        </div>
      </button>

      {open && (
        <div className="border-t border-white/10 p-5 sm:p-6 space-y-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <StatCard icon={Crosshair} label="Kill totali" value={item.raw_player_kills} />
            <StatCard icon={Crown} label="Kill MVP ufficiali" value={mvp.kills} gold />
            <StatCard icon={Medal} label="Ranking MVP" value={mvp.rank} gold />
            <StatCard icon={Swords} label="Match MVP validi" value={mvp.matches} />
          </div>

          <section>
            <h4 className="heading text-2xl text-white mb-4">Squadre e piazzamenti</h4>
            {teams.length ? (
              <div className="overflow-x-auto border border-white/10">
                <table className="w-full min-w-[650px]">
                  <thead>
                    <tr className="bg-black/50 border-b border-[#22d3ee]/30 font-teko text-lg text-[#67e8f9] tracking-wide">
                      <th className="text-left px-4 py-3">Squadra</th>
                      <th className="text-right px-4 py-3">Rank</th>
                      <th className="text-right px-4 py-3">Kill team</th>
                      <th className="text-right px-4 py-3">Vittorie</th>
                      <th className="text-right px-4 py-3">Miglior piazz.</th>
                      <th className="text-right px-4 py-3">Punti</th>
                    </tr>
                  </thead>
                  <tbody>
                    {teams.map((team, teamIndex) => (
                      <tr
                        key={`${team.name}-${team.tag}-${teamIndex}`}
                        className="border-b border-white/5 font-rajdhani"
                      >
                        <td className="px-4 py-3 text-white">
                          {team.name || "—"}{" "}
                          {team.tag && <span className="text-[#67e8f9] text-xs">[{team.tag}]</span>}
                        </td>
                        <td className="px-4 py-3 text-right font-oswald">{valueOrDash(team.rank)}</td>
                        <td className="px-4 py-3 text-right font-oswald">{valueOrDash(team.kills)}</td>
                        <td className="px-4 py-3 text-right font-oswald">{valueOrDash(team.wins)}</td>
                        <td className="px-4 py-3 text-right font-oswald">
                          {valueOrDash(team.best_placement)}
                        </td>
                        <td className="px-4 py-3 text-right font-oswald text-[#67e8f9]">
                          {valueOrDash(team.points)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="font-rajdhani text-gray-500">Nessuna squadra disponibile.</p>
            )}
          </section>

          <section>
            <h4 className="heading text-2xl text-white mb-4">Risultati partita</h4>
            {results.length ? (
              <div className="overflow-x-auto border border-white/10">
                <table className="w-full min-w-[760px]">
                  <thead>
                    <tr className="bg-black/50 border-b border-[#22d3ee]/30 font-teko text-lg text-[#67e8f9] tracking-wide">
                      <th className="text-left px-4 py-3">Match</th>
                      <th className="text-left px-4 py-3">Squadra</th>
                      <th className="text-left px-4 py-3">Mappa</th>
                      <th className="text-right px-4 py-3">Piazz.</th>
                      <th className="text-right px-4 py-3">Kill player</th>
                      <th className="text-right px-4 py-3">Kill team</th>
                      <th className="text-right px-4 py-3">Punti team</th>
                    </tr>
                  </thead>
                  <tbody>
                    {results.map((result, resultIndex) => (
                      <tr
                        key={`${result.match_number}-${result.team_name}-${resultIndex}`}
                        className="border-b border-white/5 font-rajdhani"
                      >
                        <td className="px-4 py-3 text-white">
                          #{valueOrDash(result.match_number)}
                          {result.phase && (
                            <span className="block text-[10px] uppercase text-gray-500">
                              {result.phase}
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-white">
                          {result.team_name || "—"}{" "}
                          {result.team_tag && (
                            <span className="text-[#67e8f9] text-xs">[{result.team_tag}]</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-gray-300">{result.map || "—"}</td>
                        <td className="px-4 py-3 text-right font-oswald">
                          {valueOrDash(result.placement)}
                        </td>
                        <td className="px-4 py-3 text-right font-oswald text-[var(--gold-light)]">
                          {valueOrDash(result.player_kills)}
                        </td>
                        <td className="px-4 py-3 text-right font-oswald">
                          {valueOrDash(result.team_kills)}
                        </td>
                        <td className="px-4 py-3 text-right font-oswald text-[#67e8f9]">
                          {valueOrDash(result.team_points)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="font-rajdhani text-gray-500">Nessun risultato pubblico disponibile.</p>
            )}
          </section>
        </div>
      )}
    </article>
  );
}

export default function StoricoPlayer() {
  const [activisionId, setActivisionId] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  async function submit(event) {
    event.preventDefault();

    const query = activisionId.trim();
    if (!query) {
      setError("Inserisci un Activision ID.");
      setData(null);
      return;
    }

    setLoading(true);
    setError("");
    setData(null);

    try {
      const response = await api.get("/public/players/history", {
        params: { activision_id: query },
      });
      setData(response.data);
    } catch (err) {
      const status = err?.response?.status;
      setError(
        status === 404
          ? "Giocatore non trovato."
          : status === 400
            ? "Inserisci un Activision ID valido."
            : "Impossibile caricare lo storico del giocatore."
      );
    } finally {
      setLoading(false);
    }
  }

  const summary = data?.summary || {};
  const tournaments = data?.tournaments || [];
  const championships = data?.championships || [];

  return (
    <div className="text-white">
      <section className="relative py-20 sm:py-24 overflow-hidden">
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: `url(${IMG.cyberBg})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black via-[#0b1a34]/85 to-black" />
        <div className="absolute inset-0 grid-overlay opacity-30" />

        <div className="relative max-w-5xl mx-auto px-5 text-center">
          <span className="inline-flex items-center gap-2 font-rajdhani uppercase tracking-[0.3em] text-[#67e8f9] text-xs mb-4">
            <UserRound className="w-4 h-4" />
            Archivio competitivo SEALS + SAF
          </span>

          <h1 className="heading text-5xl sm:text-7xl lg:text-8xl">
            <span className="cyber-text">STORICO</span>{" "}
            <span className="gold-text">PLAYER</span>
          </h1>

          <p className="font-rajdhani text-gray-300 text-lg mt-4 max-w-2xl mx-auto">
            Cerca un giocatore tramite Activision ID e consulta tornei disputati, squadre,
            piazzamenti, kill, risultati, ranking, MVP e Campionati.
          </p>

          <form
            onSubmit={submit}
            className="mt-8 max-w-2xl mx-auto flex flex-col sm:flex-row gap-3"
            data-testid="player-history-search"
          >
            <input
              type="text"
              value={activisionId}
              onChange={(event) => setActivisionId(event.target.value)}
              placeholder="Activision ID, es. Player#1234567"
              autoComplete="off"
              className="flex-1 min-w-0 bg-black/70 border border-[#22d3ee]/40 focus:border-[#67e8f9] outline-none px-5 py-3 font-rajdhani text-white placeholder:text-gray-600 clip-beveled-sm"
              data-testid="player-history-input"
            />
            <button
              type="submit"
              disabled={loading}
              className="btn-cyber clip-hud px-7 py-3 inline-flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="player-history-submit"
            >
              <Search className="w-4 h-4" />
              {loading ? "Ricerca..." : "Cerca"}
            </button>
          </form>

          {error && (
            <p
              className="mt-4 font-rajdhani text-sm text-red-400"
              data-testid="player-history-error"
            >
              {error}
            </p>
          )}
        </div>
      </section>

      {data && (
        <div className="max-w-7xl mx-auto px-5 py-14 space-y-14">
          <section>
            <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-6">
              <div>
                <p className="font-rajdhani uppercase tracking-[0.25em] text-[#67e8f9] text-xs">
                  Activision ID
                </p>
                <h2 className="heading text-4xl sm:text-5xl text-white break-all">
                  {data.player?.activision_id || activisionId}
                </h2>
              </div>

              <p className="font-rajdhani text-gray-500 text-sm max-w-md md:text-right">
                Lo storico mostra esclusivamente risultati competitivi pubblicati.
              </p>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-8 gap-3">
              <StatCard icon={Trophy} label="Tornei" value={summary.tournaments} gold />
              <StatCard icon={Users} label="Squadre" value={summary.teams} />
              <StatCard icon={Gamepad2} label="Match" value={summary.matches} />
              <StatCard icon={Crosshair} label="Kill totali" value={summary.raw_kills} />
              <StatCard
                icon={Crown}
                label="Kill MVP ufficiali"
                value={summary.official_mvp_kills}
                gold
              />
              <StatCard
                icon={Trophy}
                label="Vittorie torneo"
                value={summary.tournament_wins}
                gold
              />
              <StatCard
                icon={Medal}
                label="Miglior ranking"
                value={summary.best_tournament_rank}
              />
              <StatCard
                icon={Crown}
                label="Campionati"
                value={summary.championships}
                gold
              />
            </div>
          </section>

          <section data-testid="player-history-tournaments">
            <div className="flex items-center gap-3 mb-6">
              <Swords className="w-5 h-5 text-[#67e8f9]" />
              <h2 className="heading text-4xl text-white">
                Tornei <span className="cyber-text">Disputati</span>
              </h2>
            </div>

            {tournaments.length ? (
              <div className="space-y-4">
                {tournaments.map((item, index) => (
                  <TournamentHistory
                    key={item.tournament?.id || index}
                    item={item}
                    index={index}
                  />
                ))}
              </div>
            ) : (
              <div className="cyber-panel clip-hud p-8 text-center">
                <Gamepad2 className="w-10 h-10 text-gray-600 mx-auto mb-3" />
                <p className="font-rajdhani text-gray-400">
                  Nessun torneo con risultati pubblici disponibile per questo giocatore.
                </p>
              </div>
            )}
          </section>

          <section data-testid="player-history-championships">
            <div className="flex items-center gap-3 mb-6">
              <Crown className="w-5 h-5 text-[var(--gold)]" />
              <h2 className="heading text-4xl text-white">
                Campionati <span className="gold-text">MVP</span>
              </h2>
            </div>

            {championships.length ? (
              <div className="grid md:grid-cols-2 gap-5">
                {championships.map((championship) => (
                  <div
                    key={championship.id}
                    className="panel panel-gold clip-hud p-6"
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <span className="font-rajdhani uppercase tracking-wider text-[10px] text-[var(--gold-light)]">
                          {STATE_LABEL[championship.state] || championship.state || "Campionato"}
                        </span>
                        <h3 className="heading text-3xl gold-text mt-1">
                          {championship.name || "Campionato"}
                        </h3>
                      </div>
                      <Crown className="w-8 h-8 text-[var(--gold)] shrink-0" />
                    </div>

                    <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mt-6">
                      {[
                        ["Rank", championship.mvp?.rank],
                        ["Kill", championship.mvp?.kills],
                        ["Punti", championship.mvp?.points],
                        ["Match", championship.mvp?.matches],
                        ["Tornei", championship.mvp?.tournaments],
                      ].map(([label, value]) => (
                        <div key={label} className="bg-black/40 border border-white/10 p-3 text-center">
                          <div className="font-oswald text-xl text-white">
                            {valueOrDash(value)}
                          </div>
                          <div className="font-rajdhani text-[9px] uppercase tracking-widest text-gray-500">
                            {label}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="font-rajdhani text-gray-500">
                Nessun Campionato pubblico disponibile per questo giocatore.
              </p>
            )}
          </section>

          <section className="cyber-panel clip-hud p-6">
            <div className="flex gap-3">
              <Target className="w-5 h-5 text-[#67e8f9] shrink-0 mt-0.5" />
              <p className="font-rajdhani text-sm text-gray-400">
                Le kill totali rappresentano i risultati pubblicati disputati dal giocatore.
                Le kill MVP ufficiali rispettano invece le regole competitive del singolo torneo,
                incluso l'eventuale Best-of.
              </p>
            </div>
          </section>
        </div>
      )}
    </div>
  );
}
